import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { decryptText, decryptAppId } from '../utils/encryption';
import s3Utils from '../utils/s3Utils';

const API_URL = process.env.REACT_APP_API_URL;

const Form72Report = () => {
  const location = useLocation();

  const [appId] = useState(() => {
    const v = new URLSearchParams(location.search).get('appId');
    if (!v) return null;
    try { return decryptAppId(v); } catch { return v; }
  });
  const [orgId] = useState(() => {
    const v = new URLSearchParams(location.search).get('orgId');
    if (!v) return null;
    try { return decryptText(v); } catch { return v; }
  });

  const [assessmentId] = useState(() => {
    const v = new URLSearchParams(location.search).get('assessmentId');
    if (!v) return null;
    try { return decryptText(v); } catch { return v; }
  });

  const [status, setStatus] = useState('checking');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (appId && assessmentId) {
      initForm72();
    } else {
      setStatus('error');
      setErrorMsg('Missing appId or assessmentId in URL');
    }
  }, []);

  const initForm72 = async () => {
    try {
      // Check if URL already saved in DB
      const checkRes = await axios.get(`${API_URL}/form72url/check/${appId}`);
      if (checkRes.data.exists && checkRes.data.file72url) {
        window.location.href = checkRes.data.file72url;
        return;
      }

      // Generate PDF
      setStatus('generating');
      const reportData = await fetchReportData();
      const pdfBlob = await generatePDF(reportData);

      // Upload to S3
      setStatus('uploading');
      const now = new Date();
      const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}${String(now.getSeconds()).padStart(2,'0')}`;
      const fileName = `Form217_${appId}_${ts}.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
      const uploadResult = await s3Utils.uploadFile(file, 'live');

      if (!uploadResult.success) throw new Error('S3 upload failed');

      // Use exact URL returned by backend (direct public S3 URL)
      const s3Url = uploadResult.url;
      if (!s3Url) throw new Error('No URL returned from upload');

      // Save URL to DB
      await axios.post(`${API_URL}/form72url/save`, {
        AppId: appId,
        OrgId: orgId,
        Url: s3Url
      });

      // Navigate to direct S3 URL
      window.location.href = s3Url;

    } catch (error) {
      console.error('Form72 error:', error);
      setErrorMsg(error.message);
      setStatus('error');
    }
  };

  const fetchReportData = async () => {
    const response = await axios.get(`${API_URL}/nc-main/form72/${appId}/${assessmentId}`);
    const apiData = response.data;

    const selfiesWithUrls = apiData.selfi ? await Promise.all(
      apiData.selfi.map(async (item) => ({
        LaboratoryName: item.laboratoryName || '',
        Name: item.name || '',
        Capacity: item.capacity || '',
        DateFrom: item.dateFrom || '',
        StartDate: item.startDate || '',
        StartSelfie: item.startSelfie ? await s3Utils.generateSignedUrlForFilename(item.startSelfie) : '',
        StartLat: item.startLatitude || '',
        StartLong: item.startLongitude || '',
        StopDate: item.stopDate || '',
        StopSelfie: item.stopSelfie ? await s3Utils.generateSignedUrlForFilename(item.stopSelfie) : '',
        StopLat: item.stopLatitude || '',
        StopLong: item.stopLongitude || ''
      }))
    ) : [];

    return {
      selfies: selfiesWithUrls,
      naf6: {
        lblNAF6LabName: 'Laboratory assessed',
        lblNaf6Lab: apiData.laboratoryName || '',
        lblNaf6QM: apiData.qm || '',
        lblNAF6DateOfVisit: 'Date(s) of Visit',
        lblNaf6Date: apiData.assessmentDate || '',
        lblsubmissionDate: apiData.dateOfSubmission || '',
        lblNAF6TypeOfVisit: 'Type of Visit',
        lblNaf6AssessmenttYPE: apiData.assessmentType || '',
        lblNaf6field: apiData.labType || '',
        lblNaf6Discipline: apiData.disciplines || '',
        lblNaf6Fcility: apiData.facility || '',
        lblNaf6LA: apiData.laName || '',
        rptrNaf6: apiData.naf6?.map(item => ({
          Capacity: item.capacity || '',
          Name: item.name || ''
        })) || [],
        lblNaf6PrevVisitDate: apiData.previousVisitDate || '',
        lblNonConformities: apiData.nonConformities || '',
        lblNaf6AssmtSmry: apiData.assessmentSummary || '',
        lblAssmntTeam: apiData.assessmentTeamComment || '',
        lblNaf6NABL133: apiData.nabL133 || '',
        lblNaf6Nabl142: apiData.nabL142 || '',
        lblNaf6Nabl163: apiData.nabL163 || '',
        lblNaf6Nabl143: apiData.nabL143 || '',
        linbl143: apiData.showNABL143 ? true : false,
        CalibNote: apiData.showCalibrationNote ? true : false,
        lblNonConformty: 'Non-Conformities raised during the assessment',
        lblRecom: '',
        lblNaf6Recommend: apiData.recommendationOfAssessmentTeam || '',
        major: apiData.major || '0',
        minor: apiData.minor || '0',
        majorC4: apiData.majorC4 || '0',
        majorC5: apiData.majorC5 || '0',
        majorC6: apiData.majorC6 || '0',
        majorC7: apiData.majorC7 || '0',
        majorC8: apiData.majorC8 || '0',
        minorC4: apiData.minorC4 || '0',
        minorC5: apiData.minorC5 || '0',
        minorC6: apiData.minorC6 || '0',
        minorC7: apiData.minorC7 || '0',
        minorC8: apiData.minorC8 || '0'
      },
      naf4: apiData.rptr || [],
      naf3a: apiData.naf3a || [],
      naf1a: {
        cabName: apiData.laboratoryName || '',
        openingDate: apiData.openingDate || '',
        openingTime: apiData.openingTime || '',
        closingDate: apiData.closingDate || '',
        closingTime: apiData.closingTime || '',
        attendanceList: apiData.naf1AMeeting?.map(item => ({
          meeting: item.meeting || '',
          labpersonal: item.labPersonal || '',
          designation: item.designation || ''
        })) || []
      },
      form72: apiData.data?.map(item => ({
        AssessorName: item.AssessorName || '',
        LabLocationName: '',
        disciplineName: item.disciplineName || '',
        DisciplineId: item.DisciplineId || '',
        GroupId: '',
        testCode: item.TestCode || '',
        GroupTested: item.GroupTested || '',
        cv: item.CV || '',
        mu: item.MU || '',
        rangeType: item.RangeType || '',
        testPerformedAt: item.TestPerformedAt || '',
        scopeStandard: item.ScopeStandard || '',
        groupName: item.GroupTested || '',
        SubGroupId: '',
        subGrpName: '',
        TypeOfTest: item.TypeOfTest || '',
        scopeyear: '',
        unit: `${item.RangeLLUnit || ''}${item.RangeULUnit ? ' - ' + item.RangeULUnit : ''}`,
        measr: item.MU || '',
        status: item.InformationStatus || '',
        assessmentTechniqueUsed: item.AssessmentTechniqueUsed || item.ScopeStandard || '',
        Remarks: item.Observation || ''
      })) || [],
      form74: apiData.dec?.map(item => ({
        Name: item.name || '',
        isExEmployee: item.isExEmployee || '0',
        isCabRelation: item.isCabRelation || '0'
      })) || []
    };
  };

  const generatePDF = (reportData) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 15;

    const checkPageBreak = (requiredSpace = 20) => {
      if (yPos + requiredSpace > pageHeight - 20) {
        doc.addPage();
        yPos = 15;
      }
    };

    // Title
    doc.setFillColor(227, 242, 253);
    doc.rect(0, 0, pageWidth, 14, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Scope of Accreditation (Form 72)', pageWidth / 2, 9, { align: 'center' });
    yPos = 20;

    if (reportData.form72 && reportData.form72.length > 0) {
      const groupedData = reportData.form72.reduce((acc, item) => {
        const key = `${item.AssessorName}_${item.disciplineName}`;
        if (!acc[key]) {
          acc[key] = { assessor: item.AssessorName, discipline: item.disciplineName, scopes: [] };
        }
        acc[key].scopes.push(item);
        return acc;
      }, {});

      const statusColors = {
        'Adequate': [76, 175, 80],
        'Inadequate': [244, 67, 54],
        'Not Applicable': [255, 152, 0],
      };

      let serialNumber = 0;

      Object.values(groupedData).forEach((group) => {
        checkPageBreak(35);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`Assessor: ${group.assessor || '-'}`, 10, yPos);
        yPos += 5;
        doc.text(`Discipline: ${group.discipline || '-'}`, 10, yPos);
        yPos += 4;

        const tableBody = group.scopes.map((item) => {
          serialNumber++;

          const isCalculative =
            item.rangeType === 'Calculative' ||
            item.rangeType === 'Calculated' ||
            (item.scopeStandard && item.scopeStandard.toLowerCase().includes('calculated'));

          const scopeDetails = [
            `Test Code: ${item.testCode || '-'}`,
            `Materials: ${item.GroupTested || '-'}`,
            `Test Performed: ${item.TypeOfTest || '-'}`,
            `Method: ${item.scopeStandard || '-'}`
          ].join('\n');

          const rangeStandards = [
            `Type: ${item.rangeType || '-'}`,
            `Facility: ${item.testPerformedAt || '-'}`,
            `Range: ${item.unit || '-'}`,
            ...(!isCalculative ? [`%CV: ${item.cv || '-'}`] : [])
          ].join('\n');

          const assessmentDetails = [
            `Assessment Technique: ${item.assessmentTechniqueUsed || item.scopeStandard || '-'}`,
            ...(item.Remarks ? [`Remarks: ${item.Remarks}`] : [])
          ].join('\n');

          return [
            { content: String(serialNumber), styles: { halign: 'center', valign: 'middle' } },
            { content: scopeDetails },
            { content: rangeStandards },
            { content: assessmentDetails },
            {
              content: item.status || 'N/A',
              styles: {
                halign: 'center',
                valign: 'middle',
                textColor: [255, 255, 255],
                fillColor: statusColors[item.status] || [158, 158, 158],
                fontStyle: 'bold'
              }
            }
          ];
        });

        autoTable(doc, {
          startY: yPos,
          head: [[
            { content: 'Sr No.', styles: { halign: 'center' } },
            { content: 'Scope Details', styles: { halign: 'center' } },
            { content: 'Range & Standards', styles: { halign: 'center' } },
            { content: 'Assessment Details', styles: { halign: 'center' } },
            { content: 'Status', styles: { halign: 'center' } }
          ]],
          body: tableBody,
          styles: { fontSize: 7, cellPadding: 2, valign: 'top', lineColor: [189, 189, 189], lineWidth: 0.3 },
          headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 12 },
            1: { cellWidth: 52 },
            2: { cellWidth: 38 },
            3: { cellWidth: 52 },
            4: { cellWidth: 22 }
          },
          margin: { left: 10, right: 10 },
          theme: 'grid',
        });

        yPos = doc.lastAutoTable.finalY + 8;
      });
    }

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
    }

    return doc.output('blob');
  };

  const statusMessages = {
    checking: 'Checking document...',
    generating: 'Generating document...',
    uploading: 'Uploading document...',
    done: 'Document opened in new tab.',
    error: `Error: ${errorMsg}`
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      gap={2}
    >
      {status !== 'done' && status !== 'error' && (
        <CircularProgress size={60} thickness={4} />
      )}
      <Typography
        variant="h6"
        color={status === 'error' ? 'error' : 'primary'}
      >
        {statusMessages[status]}
      </Typography>
      {(status === 'done' || status === 'error') && (
        <Button variant="outlined" onClick={() => window.history.back()}>
          Go Back
        </Button>
      )}
    </Box>
  );
};

export default Form72Report;